import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import { 
  ScanBarcode, 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Package,
  Clock,
  Copy,
  RefreshCw,
  Volume2,
  VolumeX,
  Zap,
  ZapOff,
  Settings2
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Audio feedback (using Web Audio API for reliability)
const playBeep = (success) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = success ? 800 : 300;
    oscillator.type = "sine";
    gainNode.gain.value = 0.3;
    
    oscillator.start();
    oscillator.stop(audioContext.currentTime + (success ? 0.1 : 0.3));
  } catch (e) {
    console.log("Audio not supported");
  }
};

// Normalize input (strip whitespace, optionally keep only alphanumeric)
const normalizeInput = (input, stripWhitespace = true, alphanumericOnly = true) => {
  let result = input;
  
  if (stripWhitespace) {
    result = result.trim().replace(/[\n\r\t]/g, '');
  }
  
  if (alphanumericOnly) {
    result = result.replace(/[^A-Za-z0-9]/g, '');
  }
  
  return result;
};

export default function ScanStation() {
  const { getAuthHeader } = useAuth();
  const [scanInput, setScanInput] = useState("");
  const [lastScan, setLastScan] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [stationId, setStationId] = useState(() => localStorage.getItem("station_id") || `station-${Date.now()}`);
  const [showSettings, setShowSettings] = useState(false);
  const inputRef = useRef(null);
  
  // Autoscan settings (loaded from backend)
  const [autoscanSettings, setAutoscanSettings] = useState({
    scan_autosubmit_enabled: true,
    scan_idle_submit_ms: 200,
    scan_submit_cooldown_ms: 200,
    scan_min_length: 6,
    scan_strip_whitespace: true,
    scan_alphanumeric_only: true,
    scan_token_length: null
  });
  
  // Idle timer and cooldown refs
  const idleTimerRef = useRef(null);
  const cooldownRef = useRef(false);
  const lastSubmitTimeRef = useRef(0);

  // Fetch station ID from backend settings first, fallback to localStorage
  useEffect(() => {
    const fetchStationId = async () => {
      try {
        const res = await axios.get(`${API}/admin/app-settings`, { withCredentials: true });
        if (res.data.station_id && res.data.station_id !== 'DEFAULT') {
          setStationId(res.data.station_id);
          localStorage.setItem("station_id", res.data.station_id);
        }
      } catch (error) {
        console.log("Failed to fetch station ID from settings, using localStorage");
        // Keep existing localStorage value
      }
    };
    fetchStationId();
  }, []);

  // Store station ID
  useEffect(() => {
    localStorage.setItem("station_id", stationId);
  }, [stationId]);

  // Fetch autoscan settings
  const fetchAutoscanSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/station-settings/autoscan`, { withCredentials: true });
      setAutoscanSettings(res.data);
    } catch (error) {
      console.error("Failed to fetch autoscan settings, using defaults");
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scan-history?limit=10&station_id=${stationId}`, { 
        withCredentials: true
      });
      setScanHistory(res.data);
    } catch (error) {
      console.error("Failed to fetch scan history");
    }
  }, [stationId]);

  useEffect(() => {
    fetchAutoscanSettings();
    fetchHistory();
    // Keep input focused (critical for NFC keyboard wedge)
    const interval = setInterval(() => {
      if (inputRef.current && document.activeElement !== inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
    return () => clearInterval(interval);
  }, [fetchAutoscanSettings, fetchHistory]);

  // Submit scan to backend
  const submitScan = useCallback(async (scannedValue, method = "AUTOSUBMIT_IDLE") => {
    if (processing || cooldownRef.current) return;
    if (!scannedValue.trim()) return;
    
    // Check cooldown
    const now = Date.now();
    if (now - lastSubmitTimeRef.current < autoscanSettings.scan_submit_cooldown_ms) {
      return;
    }
    
    setProcessing(true);
    cooldownRef.current = true;
    lastSubmitTimeRef.current = now;
    setScanInput("");
    
    try {
      const res = await axios.post(`${API}/scan`, {
        raw_uid_or_token: scannedValue,
        station_id: stationId,
        scan_method: method
      }, { withCredentials: true });
      
      const result = {
        success: true,
        ...res.data,
        timestamp: new Date().toISOString()
      };
      setLastScan(result);
      
      // Audio feedback
      if (soundEnabled) playBeep(true);
      
      // Visual feedback via toast
      if (res.data.duplicate) {
        toast.info("Duplicate scan ignored", { duration: 2000 });
      } else if (res.data.request_created) {
        toast.warning(`REORDER CREATED: ${res.data.item_name}`, { 
          icon: <AlertTriangle className="w-4 h-4 text-yellow-400" />,
          duration: 5000 
        });
      } else if (res.data.below_reorder_point) {
        toast.warning(`LOW STOCK: ${res.data.item_name}`, { duration: 3000 });
      } else {
        toast.success(`✓ ${res.data.item_name} (-${res.data.depleted || res.data.depleted_qty || 1})`, { duration: 2000 });
      }
      
      fetchHistory();
    } catch (error) {
      const detail = error.response?.data?.detail || "Scan failed";
      let errorType = "UNKNOWN_ERROR";
      let errorMessage = detail;
      
      // Parse error type from detail
      if (detail.includes("UNKNOWN_TAG")) {
        errorType = "UNKNOWN_TAG";
        errorMessage = "Tag not registered in system";
      } else if (detail.includes("ITEM_DISABLED")) {
        errorType = "ITEM_DISABLED";
        errorMessage = "This item is disabled";
      } else if (detail.includes("Rate limit")) {
        errorType = "RATE_LIMIT";
        errorMessage = "Too many scans - please slow down";
      }
      
      setLastScan({
        success: false,
        errorType,
        message: errorMessage,
        scanned: scannedValue,
        timestamp: new Date().toISOString()
      });
      
      // Audio feedback (error beep)
      if (soundEnabled) playBeep(false);
      
      // Visual feedback
      toast.error(`ERROR: ${errorMessage}`, { duration: 4000 });
      fetchHistory();
    } finally {
      setProcessing(false);
      // Release cooldown after cooldown period
      setTimeout(() => {
        cooldownRef.current = false;
      }, autoscanSettings.scan_submit_cooldown_ms);
    }
  }, [processing, autoscanSettings, stationId, getAuthHeader, soundEnabled, fetchHistory]);

  // Handle input change with idle-detect auto-submit
  const handleInputChange = useCallback((e) => {
    const rawValue = e.target.value;
    setScanInput(rawValue);
    
    if (!autoscanSettings.scan_autosubmit_enabled) return;
    
    // Clear existing idle timer
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    
    // Normalize input
    const normalized = normalizeInput(
      rawValue,
      autoscanSettings.scan_strip_whitespace,
      autoscanSettings.scan_alphanumeric_only
    );
    
    // Check fixed-length trigger (optional secondary trigger)
    if (autoscanSettings.scan_token_length && normalized.length >= autoscanSettings.scan_token_length) {
      submitScan(rawValue, "AUTOSUBMIT_LENGTH");
      return;
    }
    
    // Set idle timer (primary trigger)
    if (normalized.length >= autoscanSettings.scan_min_length) {
      idleTimerRef.current = setTimeout(() => {
        if (!cooldownRef.current && !processing) {
          submitScan(rawValue, "AUTOSUBMIT_IDLE");
        }
      }, autoscanSettings.scan_idle_submit_ms);
    }
  }, [autoscanSettings, submitScan, processing]);

  // Handle manual Enter key (fallback)
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Enter" && scanInput.trim() && !processing) {
      // Clear idle timer since user pressed Enter
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      submitScan(scanInput, "MANUAL_ENTER");
    }
  }, [scanInput, processing, submitScan]);

  // Cleanup idle timer on unmount
  useEffect(() => {
    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, []);

  const getStatusBadge = (scan) => {
    if (scan.is_duplicate) {
      return <Badge variant="outline" className="text-blue-400 border-blue-400/50">DUPLICATE</Badge>;
    }
    if (!scan.success) {
      return <Badge variant="destructive">FAILED</Badge>;
    }
    if (scan.message?.includes("DEPLETION") || scan.success) {
      return <Badge className="bg-green-900/50 text-green-400 border-green-400/50">SUCCESS</Badge>;
    }
    return <Badge variant="outline">UNKNOWN</Badge>;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const d = new Date(timestamp);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <div className="h-16 border-b border-border px-6 flex items-center justify-between bg-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 border border-primary/30 rounded-sm flex items-center justify-center">
            <ScanBarcode className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-2xl tracking-tight">SCAN STATION</h1>
            <p className="text-xs text-muted-foreground">Station: {stationId.slice(0, 12)}...</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Autoscan indicator */}
          <div 
            className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
              autoscanSettings.scan_autosubmit_enabled 
                ? "bg-green-900/30 text-green-400" 
                : "bg-gray-900/30 text-gray-400"
            }`}
            title={autoscanSettings.scan_autosubmit_enabled 
              ? `Autoscan ON (${autoscanSettings.scan_idle_submit_ms}ms idle)` 
              : "Autoscan OFF - Press Enter to submit"}
            data-testid="autoscan-indicator"
          >
            {autoscanSettings.scan_autosubmit_enabled ? (
              <Zap className="w-3 h-3" />
            ) : (
              <ZapOff className="w-3 h-3" />
            )}
            <span>{autoscanSettings.scan_autosubmit_enabled ? "AUTO" : "MANUAL"}</span>
          </div>
          
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setShowSettings(!showSettings)}
            title="Scan settings"
            data-testid="toggle-settings-btn"
          >
            <Settings2 className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setSoundEnabled(!soundEnabled)}
            title={soundEnabled ? "Mute sounds" : "Enable sounds"}
            data-testid="toggle-sound-btn"
          >
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4 text-muted-foreground" />}
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={fetchHistory}
            title="Refresh history"
            data-testid="refresh-history-btn"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Settings Panel (collapsible) */}
      {showSettings && (
        <div className="border-b border-border bg-card/50 px-6 py-3">
          <div className="flex flex-wrap items-center gap-4 text-xs">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Idle Submit:</span>
              <span className="font-mono text-primary">{autoscanSettings.scan_idle_submit_ms}ms</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Min Length:</span>
              <span className="font-mono text-primary">{autoscanSettings.scan_min_length}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Cooldown:</span>
              <span className="font-mono text-primary">{autoscanSettings.scan_submit_cooldown_ms}ms</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Dedup:</span>
              <span className="font-mono text-primary">{autoscanSettings.scan_dedup_window_seconds}s</span>
            </div>
            {autoscanSettings.scan_token_length && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Fixed Length:</span>
                <span className="font-mono text-primary">{autoscanSettings.scan_token_length}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Normalize:</span>
              <span className="font-mono text-primary">
                {autoscanSettings.scan_alphanumeric_only ? "Alnum" : "All"}
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Scan Area */}
        <div className="flex-1 flex flex-col items-center justify-center p-8 relative">
          {/* CRT Effect Overlay */}
          <div className="absolute inset-0 crt-effect z-10" />
          
          <div className="relative z-20 w-full max-w-2xl space-y-8">
            {/* Scan Input */}
            <div className="space-y-4">
              <label className="block text-center text-sm font-bold uppercase tracking-wider text-muted-foreground">
                Scan NFC Tag or QR Code
              </label>
              <Input
                ref={inputRef}
                value={scanInput}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={processing ? "Processing..." : "Waiting for scan..."}
                className={`scan-input text-center ${processing ? "opacity-50" : ""}`}
                autoFocus
                disabled={processing}
                data-testid="scan-input"
              />
              <p className="text-center text-xs text-muted-foreground">
                {autoscanSettings.scan_autosubmit_enabled ? (
                  <>Place card on reader or scan QR code. <span className="text-green-400">Auto-submits when typing stops.</span></>
                ) : (
                  <>Place card on reader or scan QR code. Press Enter to process.</>
                )}
              </p>
            </div>
            
            {/* Last Scan Result */}
            {lastScan && (
              <div 
                className={`p-6 rounded-sm border-2 transition-all ${
                  lastScan.success 
                    ? lastScan.duplicate 
                      ? "border-blue-500/50 bg-blue-900/10" 
                      : "border-green-500/50 bg-green-900/10" 
                    : "border-red-500/50 bg-red-900/10"
                } ${processing ? "" : "animate-scan-flash"}`}
                data-testid="last-scan-result"
              >
                <div className="flex items-start gap-4">
                  {lastScan.success ? (
                    lastScan.duplicate ? (
                      <Copy className="w-10 h-10 text-blue-500 shrink-0" />
                    ) : lastScan.request_created ? (
                      <AlertTriangle className="w-10 h-10 text-yellow-500 shrink-0" />
                    ) : (
                      <CheckCircle className="w-10 h-10 text-green-500 shrink-0" />
                    )
                  ) : (
                    <XCircle className="w-10 h-10 text-red-500 shrink-0" />
                  )}
                  
                  <div className="flex-1">
                    {lastScan.success ? (
                      <>
                        <h2 className={`text-2xl font-heading uppercase ${
                          lastScan.duplicate ? "text-blue-400" :
                          lastScan.request_created ? "text-yellow-400" : "text-green-400"
                        }`}>
                          {lastScan.duplicate ? "DUPLICATE SCAN" :
                           lastScan.request_created ? "REORDER CREATED" :
                           lastScan.below_reorder_point ? "LOW STOCK" : "SUCCESS"}
                        </h2>
                        <p className="text-lg">{lastScan.item_name}</p>
                        {!lastScan.duplicate && (
                          <>
                            <p className="text-sm text-muted-foreground">
                              Depleted: <span className="font-mono">{lastScan.depleted || lastScan.depleted_qty || "-"}</span> | 
                              New Balance: <span className="font-mono">{lastScan.new_balance ?? lastScan.new_on_hand}</span>
                            </p>
                            {lastScan.request_created && (
                              <p className="text-sm text-yellow-400 mt-1">
                                ⚠ Replenishment request has been created
                              </p>
                            )}
                          </>
                        )}
                        {lastScan.duplicate && (
                          <p className="text-sm text-muted-foreground">
                            This scan was already processed (dedup window)
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <h2 className="text-2xl font-heading uppercase text-red-400">
                          {lastScan.errorType || "ERROR"}
                        </h2>
                        <p className="text-lg">{lastScan.message}</p>
                        <p className="text-sm text-muted-foreground font-mono mt-1">
                          Input: {lastScan.scanned}
                        </p>
                      </>
                    )}
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatTime(lastScan.timestamp)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* History Sidebar */}
        <div className="w-80 border-l border-border bg-card/50 flex flex-col">
          <div className="p-4 border-b border-border">
            <h2 className="font-heading text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
              <Clock className="w-4 h-4" />
              LAST 10 SCANS
            </h2>
          </div>
          
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {scanHistory.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No scans yet</p>
                </div>
              ) : (
                scanHistory.map((scan, idx) => (
                  <div 
                    key={scan.id || idx}
                    className={`p-3 rounded-sm border ${
                      scan.is_duplicate ? "border-blue-900/30 bg-blue-900/5" :
                      scan.success ? "border-green-900/30 bg-green-900/5" : "border-red-900/30 bg-red-900/5"
                    }`}
                    data-testid={`scan-history-item-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-sm truncate">
                        {scan.item_sku || scan.item_name || scan.raw_uid_or_token?.slice(0, 12) + "..."}
                      </span>
                      {getStatusBadge(scan)}
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{scan.message?.slice(0, 25) || (scan.success ? "OK" : "Failed")}</span>
                      <span>{formatTime(scan.timestamp)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
